import { useEffect, useMemo, useRef, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { sampleTokens } from '../lib/sampleTokens.ts';
import { pickBrandDraft, mergeExtractedTokens } from '../lib/brandDraft.ts';
import { loadCurrentTokens, isRecent } from '../lib/clientTokens.ts';
import { BuildStatus } from './BuildStatus.tsx';
import { BrandKit } from './BrandKit.tsx';
import { BrandIntake } from './BrandIntake.tsx';
import { BrandPreview } from './BrandPreview.tsx';
import { BuildChat } from './BuildChat.tsx';
import { FlapText } from './FlapText.tsx';
import { PageManager } from './PageManager.tsx';
import { reviewComplete, activePageId } from '../lib/sections.ts';
import { STEPS, landingStepKey, type WizardState } from '../lib/wizardSteps.ts';
import { isDeployBuild } from '../lib/deployJobs.ts';
import { MediaUploader } from './MediaUploader.tsx';
import { AssetsUploader } from './AssetsUploader.tsx';
import { ResponsiveChecklist } from './ResponsiveChecklist.tsx';
import { GoLiveChecklist } from './GoLiveChecklist.tsx';
import { goliveProgress } from '../lib/goliveChecklist.ts';
import { MediaStructuring } from './MediaStructuring.tsx';
import { VIEWPORTS } from '../lib/responsiveMatrix.ts';
import { canMaintainLive } from '../lib/liveMaintain.ts';
import { brandPublishResume, inFlightBuildId, canPublishUpdate } from '../lib/resumeFlows.ts';
import { applyFigmaTokens } from '../lib/figmaConfirm.ts';
import type { TokenSchema, FontFace } from '../lib/tokenSchema.ts';
import type { Build, Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, btnGhost, sectionDesc } from '../lib/ui.ts';

/**
 * Guided build wizard (spine) for one client. Plain-language, gated, and
 * resumable: progress (current step + which steps are done) persists in
 * clients.wizard_state via /api/wizard-state. A step only unlocks once the
 * REQUIRED steps before it are done, so a designer can't skip ahead.
 *
 * Flow (IA-first, IA-2): Add client → Brand kit → Content & media (skippable) →
 * Site structure (pages · menu · footer) → SEO (optional) → Build (the build chat,
 * the real site is assembled cumulatively here) → Preview & approve → Go live →
 * Handover. Structure-first, then SEO, then Build — so each page self-SEOs by
 * construction (the handoff doc already exists when it's built). Add-client seeds a
 * BLANK canvas; the brand kit pushes the locked tokens; Build composes the real site
 * into that canvas section by section. (The step key stays `review` for back-compat.)
 */

// The step spine (STEPS), the WizardState shape, and landingStepKey now live in the pure
// ../lib/wizardSteps.ts module (no React/supabase) so the gating/landing rules are
// unit-testable — the component just consumes them (imported above).

interface BuildResponse {
  buildId: string;
  outcome: string;
  branch: string | null;
  assembly: { notes: string[]; files: string[] };
}

// SEO is now the conversational instance (the BuildChat component, instance="seo")
// — the one-shot facts form + its async-status types are retired (chat-engine.md §8.2).

export function Wizard({
  client,
  builds,
  onActivity,
  isAdmin = false,
}: {
  client: Client;
  builds: Build[];
  onActivity: () => void;
  isAdmin?: boolean;
}) {
  // #GR88: an ADMIN may edit + re-lock a LIVE client's brand — the sanctioned live carve-out
  // (maintain-flow.md §5). Edits preview on app/<slug>; publishing is the explicit Go-live below.
  // A non-admin on a live client stays fully locked out (unchanged). Cooper is protected-slug
  // excluded server-side (brand-relock + maintain-golive both refuse it) regardless of this flag.
  // go-live-protection §3a frontend unfreeze: an admin may MAINTAIN a maintainable LIVE client
  // — the EXACT backend liveGuard('site-write') allow-condition (isAdmin && isMaintainableSlug).
  // Edits land on the preview branch; the live site only changes on a deliberate publish. Passed
  // to the edit surfaces below so they show the controls that now work and keep hidden the ones
  // that still 403 (non-admin / protected slug). The real enforcement stays server-side.
  const maintainLive = canMaintainLive(client, isAdmin);
  // #GR88: an ADMIN may edit + re-lock a LIVE client's brand (the carve-out). Now via maintainLive
  // (adds the isMaintainableSlug check — a protected live slug stays locked, matching brand-relock).
  const adminLiveBrandEdit = client.is_live && maintainLive;
  // Brand controls are disabled only when live AND not the admin carve-out (i.e. non-admins on a
  // live client). The admin carve-out ENABLES the editor + routes lock through brand-relock.
  const brandControlsLocked = client.is_live && !adminLiveBrandEdit;
  const [ws, setWs] = useState<WizardState>((client.wizard_state as WizardState) ?? {});
  // The brand kit's tokens, edited via the Brand-kit page (not raw JSON).
  const [tokens, setTokens] = useState<TokenSchema>(() => sampleTokens());
  const [active, setActive] = useState<string>('add_client');
  // IA-2: the active page for the standalone "Site structure" step (the PageManager is
  // controlled). Build keeps its own; both persist the view-pointer server-side.
  const [structurePage, setStructurePage] = useState<string>(() => activePageId(client.wizard_state));
  // Bumped whenever the uploader catalogues new assets, so the structuring view
  // (a sibling) reloads its groups + media without a remount.
  const [mediaVersion, setMediaVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Decision C: a green preview of app/<slug> exists (the single source of truth the publish/lock
  // path uses — polled via /api/site-status). Gates the approve button. Set by the preview chip's
  // onResolved (fires when site-status first reads 'ready'); reset per client below.
  const [previewReady, setPreviewReady] = useState(false);
  // SEO is now the conversational instance (the BuildChat component, instance="seo")
  // — no form state lives here anymore (chat-engine.md §8.2; one-shot form retired).

  // Real progress signals derived from build history (the source of truth for the
  // working steps), combined with explicitly-persisted marks below.
  // The brand kit is "done" once its tokens have been pushed to the client repo
  // (a successful deterministic assembly build = brand.ts committed), or an
  // explicit lock mark. This is what Review's build chat reads.
  const hasTokensPushed = builds.some(
    (b) => b.job_type === 'assembly' && b.outcome === 'success' && b.commit_sha,
  );
  const hasPromoted = builds.some((b) => b.promote_commit_sha);
  // Review (build chat) is done when every section it created is locked. Read from
  // the live client prop (refreshed by onActivity after each lock), not the local
  // ws snapshot, so a lock reflects without remounting.
  const reviewAllLocked = useMemo(
    // M-P2: whole-site gate — every page's sections locked (Home ≥1), pages layer or legacy flat.
    () => reviewComplete(client.wizard_state as Record<string, unknown>),
    [client.wizard_state],
  );
  // SEO is now the conversational instance: a verified handoff sets
  // wizard_state.seo.handoffReady (chat-engine.md §8.2 — the gate the chat writes).
  // The legacy one-shot seo-intake success still counts for older clients.
  const seoHandoffReady = !!(client.wizard_state as { seo?: { handoffReady?: boolean } } | null)?.seo?.handoffReady;
  const hasSeo = builds.some((b) => b.job_type === 'seo-intake' && b.outcome === 'success');

  // Decision C — the deterministic "current preview" commit: the newest SUCCESSFUL deploy build
  // (a mirror of site-status.ts's resolver — DEPLOY_JOB_TYPES ∩ success ∩ commit_sha, newest).
  // `builds` arrives newest-first (DesignerDashboard orders created_at desc), so builds[0] of the
  // filtered set is the live preview commit. Used to detect a material change to the approved preview.
  const currentPreviewSha = useMemo(() => {
    const row = builds.find((b) => isDeployBuild(b) && b.outcome === 'success' && b.commit_sha);
    return (row?.commit_sha as string | undefined) ?? null;
  }, [builds]);

  // The recorded client sign-off (go-live-protection §5). Stale when the preview's deploy commit
  // has moved on since approval (a material change) — then it no longer counts as approved and must
  // be re-done. A null-sha approval (recorded before the deploy row caught up) never goes stale.
  const approval = ws.client_approved;
  const approvalStale = !!(approval?.sha && currentPreviewSha && approval.sha !== currentPreviewSha);
  const clientApproved = !!approval && !approvalStale;

  // Responsive QA tick-count (advisory) — surfaced beside approve so "approve with 0 checks" is at
  // least visible (§5.3). Never gates; the checklist stays advisory per its own contract.
  const viewportsChecked = VIEWPORTS.filter((v) => ws.responsive?.[v.id]?.checked).length;
  const viewportsTotal = VIEWPORTS.length;

  // Go-live checklist (go-live-checklist.md) — items 1 + 9 roll up the existing responsive +
  // client-approval state (don't duplicate). The progress drives the badge + the Go-live soft gate.
  const goliveRollups = { responsiveDone: viewportsTotal > 0 && viewportsChecked === viewportsTotal, clientApproved };
  const goliveStatus = goliveProgress(ws.golive ?? {}, goliveRollups);

  function isDone(key: string): boolean {
    if (key === 'add_client') return true; // the client exists, so this is done
    // Brand kit: tokens pushed, or a lock mark (legacy `look` mark counts too).
    if (key === 'brand_kit')
      return hasTokensPushed || ws.steps?.brand_kit === 'done' || ws.steps?.look === 'done';
    if (key === 'assets') return ws.steps?.assets === 'done';
    if (key === 'content_media') return ws.steps?.content_media === 'done';
    // IA-2: "effectively free" — ≥1 page (Home) always exists, so it never gates SEO/Build.
    if (key === 'site_structure') return true;
    if (key === 'seo') return seoHandoffReady || hasSeo || ws.steps?.seo === 'done';
    if (key === 'review') return reviewAllLocked || ws.steps?.review === 'done';
    // Decision C: "done" = the explicit client sign-off (not a build-row heuristic), and it goes
    // un-done if the preview materially changed since (clientApproved folds that in). So send_live
    // only unlocks on a current, valid approval — consistent with go-live.ts requiring it. Once the
    // site is live (hasPromoted) the requirement is moot — keep Go-live/Handover reachable so a
    // post-live rebuild's staleness can't retroactively lock a promoted client out of its later steps.
    if (key === 'preview_approve') return clientApproved || hasPromoted;
    if (key === 'send_live') return hasPromoted;
    if (key === 'handover') return !!ws.handover_done;
    return ws.steps?.[key] === 'done';
  }

  // A working step is reachable only once every REQUIRED working step before it
  // is done. Optional working steps (SEO) and coming steps never gate. Coming
  // steps are always viewable (informational placeholders).
  function reachable(idx: number): boolean {
    if (STEPS[idx].kind === 'coming') return true;
    for (let i = 0; i < idx; i++) {
      if (STEPS[i].kind === 'working' && !STEPS[i].optional && !isDone(STEPS[i].key)) return false;
    }
    return true;
  }

  // When the client changes, reset from its persisted state, pre-fill the editor
  // from the client's current pushed tokens, and resume at the saved step.
  useEffect(() => {
    const next = (client.wizard_state as WizardState) ?? {};
    setWs(next);
    setErr(null);
    setPreviewReady(false); // Decision C: re-resolve the green-preview gate for the new client
    setStructurePage(activePageId(next as Record<string, unknown>)); // IA-2: reset the structure step's active page
    // Resume where they left off. Remap legacy keys from the pre-restructure flow
    // (the old `look`/`build` steps are gone) so an in-flight client lands sanely.
    // With no saved step (a brand-new client), land on THIS client's own first
    // incomplete step — never a leaked global/prior-client step.
    const legacy: Record<string, string> = { look: 'brand_kit', build: 'review' };
    const savedStep = next.step ? (legacy[next.step] ?? next.step) : null;
    const resume = savedStep && STEPS.some((s) => s.key === savedStep) ? savedStep : null;
    setActive(resume ?? landingStepKey(next));
    let cancelled = false;
    if (brandDraftTimer.current) clearTimeout(brandDraftTimer.current); // drop a pending save from the prior client
    // #GR3: prefer the in-progress brand draft (font pick + edits) over the committed
    // tokens — but ONLY while brand_kit is unlocked (pickBrandDraft's rule). Once locked,
    // the committed tokens are the truth (a stale draft must never override them).
    const draft = pickBrandDraft(next.drafts?.brandTokens, next.steps?.brand_kit === 'done');
    if (draft) {
      setTokens(draft);
    } else {
      void loadCurrentTokens(client.id).then((t) => {
        if (!cancelled) setTokens((t as TokenSchema | null) ?? sampleTokens());
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // Latest ws for the debounced draft saver (avoids a stale closure).
  const wsRef = useRef(ws);
  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);

  async function saveWizardState(next: WizardState, reload = true) {
    setWs(next);
    try {
      await callApi('wizard-state', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, wizard_state: next }),
      });
      if (reload) onActivity();
    } catch (e) {
      // Draft autosaves are best-effort; only surface hard (navigational) saves.
      if (reload) setErr(friendlyError(e, (e as Error).message));
    }
  }

  function persist(next: WizardState) {
    return saveWizardState(next, true);
  }

  // Decision C — clear a STALE sign-off. When the approved preview's deploy commit has moved on
  // (a material change — a new section built/locked after approval), the recorded client_approved
  // no longer matches reality, so drop it: the Preview & approve step reopens for a fresh sign-off
  // and go-live.ts (which requires the flag) refuses until it's re-done. Guarded to a CONFIDENT
  // mismatch (both the recorded sha AND the current deploy commit are known), so a transient/partial
  // build list can never nuke a real approval (fail-safe: when unsure, keep the approval — the
  // green-preview gate still protects the publish). One persist per material change; no loop (after
  // the clear, approval.sha is gone → the condition is false).
  useEffect(() => {
    if (approval?.sha && currentPreviewSha && approval.sha !== currentPreviewSha) {
      // Clear via explicit null, NOT key-omission: wizard-state's merge is presence-gated
      // (`if (k in incoming)`), so an omitted key is PRESERVED — only an explicit value overwrites.
      // null persists as not-approved (go-live.ts `if (!approved)` refuses; FE approval→null reopens
      // approve; the approval?.sha guard above means no re-fire loop).
      // Also drop the steps mark so the state resets COHERENTLY — landingStepKey (pure, steps-based)
      // then points back to Preview & approve for the fresh sign-off, matching the rail/reachability.
      const { preview_approve: _m, ...stepsRest } = ws.steps ?? {};
      void persist({ ...ws, client_approved: null, steps: stepsRest });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPreviewSha]);

  // #GR3: draft the in-progress brand-kit tokens to wizard_state (debounced, best-effort)
  // so a navigate-away/remount rehydrates the font pick + edits instead of resetting to
  // the committed tokens. Every brand-editor mutation routes through here.
  const brandDraftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function handleBrandTokens(next: TokenSchema) {
    setTokens(next);
    if (brandDraftTimer.current) clearTimeout(brandDraftTimer.current);
    brandDraftTimer.current = setTimeout(() => {
      const cur = wsRef.current;
      void saveWizardState({ ...cur, drafts: { ...(cur.drafts ?? {}), brandTokens: next } }, false);
    }, 600);
  }

  // #GR87: fold a fresh extraction into the current tokens. With NO artboards this run there's
  // no colour reference, so the extractor invents a palette — carry the current palette forward
  // (merge-not-clobber) instead of surfacing colours the designer never gave, and note it.
  const [paletteKept, setPaletteKept] = useState(false);
  function handleBrandExtracted(proposed: TokenSchema) {
    const hasColourReference = (wsRef.current.drafts?.brandArtboards ?? []).length > 0;
    const { tokens: merged, paletteKept: kept } = mergeExtractedTokens(proposed, tokens, hasColourReference);
    setPaletteKept(kept);
    handleBrandTokens(merged);
  }

  function go(key: string) {
    setActive(key);
    void persist({ ...ws, step: key });
  }

  function markDoneAndAdvance(key: string) {
    const steps = { ...(ws.steps ?? {}), [key]: 'done' as const };
    const idx = STEPS.findIndex((s) => s.key === key);
    const nextStep = STEPS[idx + 1]?.key ?? key;
    void persist({ ...ws, steps, step: nextStep });
    setActive(nextStep);
  }

  // Mark a step done WITHOUT advancing (content & media: a real ingest ticks it, but
  // the designer stays on the step to keep adding/editing assets). Idempotent.
  function markStepDone(key: string) {
    if (ws.steps?.[key] === 'done') return;
    void persist({ ...ws, steps: { ...(ws.steps ?? {}), [key]: 'done' as const } });
  }

  // Decision C — record the client's sign-off and advance to Go live. `sha` pins the preview's
  // current deploy commit so a later material change makes the approval stale (cleared above).
  // (Also marks the step 'done' for back-compat with any steps-based reader; isDone reads the
  // sign-off directly.) Only reachable when previewReady — a green preview exists to sign off.
  function recordApproval() {
    const steps = { ...(ws.steps ?? {}), preview_approve: 'done' as const };
    void persist({
      ...ws,
      steps,
      client_approved: { at: new Date().toISOString(), sha: currentPreviewSha },
      step: 'send_live',
    });
    setActive('send_live');
  }

  // #GR88: after an admin re-locks a LIVE client's brand, the preview build id gates the
  // "publish to the live site" (Go-live) button on a green app/<slug> preview; the go-live
  // build id then tracks the promote. Only used in the admin live carve-out.
  const [relockBuildId, setRelockBuildId] = useState<string | null>(null);
  const [brandPreviewReady, setBrandPreviewReady] = useState(false);
  const [brandGoliveBuildId, setBrandGoliveBuildId] = useState<string | null>(null);
  // Item 1 (Phase 4): the send_live step's admin Go-live tracks its promote build here
  // (mirrors brandGoliveBuildId — the exact admin promote path, not a new endpoint).
  const [sendLiveBuildId, setSendLiveBuildId] = useState<string | null>(null);

  // #GR90 — these affordances must SURVIVE a remount (tab switch, navigate to check the preview,
  // reload). The useState above is the instant click-time value; the derived resume below recovers
  // from persisted build rows. Use `optimistic ?? derived` so clicks are instant AND remounts resume.
  const brandResume = brandPublishResume(builds);
  const effRelockBuildId = relockBuildId ?? brandResume.relockBuildId;
  const effBrandGoliveBuildId = brandGoliveBuildId ?? brandResume.goliveBuildId;
  const showBrandPublish = brandResume.show || !!relockBuildId || !!brandGoliveBuildId;
  // #GR90 #3: the in-flight go-live promote chip, re-derivable after a reload mid-go-live.
  const effSendLiveBuildId = sendLiveBuildId ?? inFlightBuildId(builds, 'promote');

  // Model-A maintenance re-publish: an already-LIVE, maintainable client with a FRESH approval
  // (hand-build content update) needs a "Publish update" button — the first-go-live button only
  // renders for !is_live, so a live client otherwise falls into the Live-status branch with no way
  // to publish. The backend (/api/go-live via goLive()) already re-publishes a live client (is_live-
  // agnostic, no-op-cleans if nothing changed; go-live-protection §4); this surfaces it. The gate is
  // the pure canPublishUpdate (resumeFlows, unit-tested): OFF once nothing's pending, ON right after
  // a new approval. maintainLive = isAdmin && isMaintainableSlug.
  const showPublishUpdate = canPublishUpdate({
    isLive: client.is_live,
    maintainLive,
    clientApproved,
    approvalAt: approval?.at,
    builds,
    promoteInFlight: !!effSendLiveBuildId,
  });

  // Lock the brand kit: push the approved tokens to the client repo (deterministic
  // assembly → brand.ts), which is what Review's build chat reads. On success the
  // step is done and we advance. No website is produced here — just the tokens.
  // #GR88: for a LIVE client an admin re-locks via the brand-relock carve-out, which writes
  // ONLY to the app/<slug> preview branch (never the live site) — the normal run-build 403s
  // is_live. The live site changes only on the explicit Go-live below.
  async function lockBrandKit() {
    setErr(null);
    setBusy(true);
    try {
      if (adminLiveBrandEdit) {
        const r = await callApi<BuildResponse>('brand-relock', {
          method: 'POST',
          body: JSON.stringify({ tokens, clientId: client.id }),
        });
        setBrandPreviewReady(false);
        setBrandGoliveBuildId(null);
        setRelockBuildId(r.buildId);
        onActivity();
      } else {
        await callApi<BuildResponse>('run-build', {
          method: 'POST',
          body: JSON.stringify({ tokens, clientId: client.id }),
        });
        markDoneAndAdvance('brand_kit');
        onActivity();
      }
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // #GR88: publish the re-locked brand to the LIVE site — the §5 promote carve-out
  // (maintain-golive → runPromoteJob, which is is_live-agnostic; Cooper is refused there AND
  // by promote.yml). Gated on a green preview (brandPreviewReady) + a typed confirm.
  async function goLiveBrand() {
    setErr(null);
    setBusy(true);
    try {
      const r = await callApi<{ buildId: string }>('maintain-golive', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id }),
      });
      setBrandGoliveBuildId(r.buildId);
      onActivity();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  // Go-live-protection §4 (Decision B), PR6: the send_live Go-live button now calls the
  // /api/go-live endpoint (not bare /api/promote). go-live enforces the full gate server-side
  // (admin · isMaintainableSlug · branch exists · client_approved · green preview) AND flips
  // is_live=true on promote success — ending the "active-but-not-live/unprotected" bug plain
  // promote left (it promotes but never flips is_live). It rides the SAME gated promote
  // (runPromoteJob → promote.yml App → main ruleset); no new way to write main. It opens a
  // 'promote' job row, so the promote-status chip below tracks it unchanged. Response is
  // 202 { buildId, status } — same shape. UI gate stays isAdmin && !is_live && !hasPromoted;
  // Cooper is is_live (never shown) + protected-slug-refused at go-live/promote.yml regardless.
  async function goLive() {
    setErr(null);
    setBusy(true);
    try {
      const out = await callApi<{ status: string; buildId: string | null }>('go-live', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id }),
      });
      setSendLiveBuildId(out.buildId);
      onActivity();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function markHandover() {
    await persist({ ...ws, handover_done: true, step: 'handover' });
    onActivity();
  }

  const activeIdx = STEPS.findIndex((s) => s.key === active);
  const def = STEPS[activeIdx] ?? STEPS[0];
  // Preview/approve + go-live show the CUMULATIVE real site, built in Review by the
  // build chat — prefer the latest build-chat deploy, else any DEPLOY build (brand-kit
  // assembly / promote) so the row resolves before sections exist. #GR47: only real
  // deploys (isDeployBuild) — a conversational `seo` job's commit is [CF-Pages-Skip]
  // (no deployment) so it would make the preview chip spin forever.
  const latestBuild =
    builds.find((b) => b.job_type === 'build-chat') ??
    builds.find(isDeployBuild);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
      {/* Step rail */}
      <nav className="space-y-2.5">
        {STEPS.map((s, idx) => {
          // #GR55: the rail TICK means "done", not "won't block you". isDone stays always-free
          // for site_structure (gating — Home always exists), but the tick only shows once the
          // designer has actually visited + Continued the step (steps.site_structure === 'done').
          const done = s.key === 'site_structure' ? ws.steps?.site_structure === 'done' : isDone(s.key);
          const isActive = s.key === active;
          const open = reachable(idx);
          const coming = s.kind === 'coming';
          return (
            <button
              key={s.key}
              onClick={() => open && go(s.key)}
              disabled={!open}
              // Rail cards (brand §3): every step sits on a raised nm-tile (the lighter
              // box-in-box lift, not the heavy .card slab). Active = the bold ink fill as a
              // raised tile ("you are here"); locked/future keep the tile but dim it (opacity-40)
              // so the rail reads as one consistent stack of cards rather than cards-then-flat-rows.
              // Two dial-in calls for the staging eyeball (one-line swaps): tile-weight (nm-tile ↔ card)
              // and active-state (bg-ink fill ↔ `nm-inset` press-in, i.e. `nm-inset bg-paper text-ink`).
              className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${
                isActive
                  ? 'nm-tile bg-ink text-paper'
                  : open
                    ? 'nm-tile hover:bg-paper-2'
                    : 'nm-tile opacity-40'
              }`}
            >
              <span
                className={`flex h-5 w-5 flex-none items-center justify-center rounded-full text-[10px] ${
                  done
                    ? 'bg-success-bright text-white'
                    : isActive
                      ? 'bg-paper text-ink'
                      : 'bg-line text-ink-2'
                }`}
              >
                {done ? '✓' : coming ? '◷' : idx + 1}
              </span>
              <span className="flex flex-col">
                <span className="leading-tight">{s.label}</span>
                {coming ? (
                  <span className={`text-[10px] ${isActive ? 'text-paper/70' : 'text-ink-2'}`}>
                    coming soon
                  </span>
                ) : s.optional ? (
                  // Phase 4 item 6: hint that assets / content & media / seo are skippable
                  // (mirrors the coming-soon sublabel treatment exactly).
                  <span className={`text-[10px] ${isActive ? 'text-paper/70' : 'text-ink-2'}`}>
                    optional
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Active step panel */}
      <section className="min-w-0 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-ink">{def.label}</h2>
          <p className="text-sm text-ink-2">{def.plain}</p>
        </div>

        {def.key === 'add_client' && (
          <div className="space-y-3 card p-5 text-sm">
            <p className="text-ink">
              This is <b>{client.name}</b>’s workspace. Everything you do here only ever touches
              their site — nothing else.
            </p>
            <p className="text-ink-2">
              Neil does a one-time setup of the live site &amp; preview hosting. You can build the
              brand kit and the sections right away — the shareable preview links go live once that
              setup is done.
            </p>
            {client.is_live && (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-warning">
                🔒 This site is live and protected — building is turned off. Ask Neil before making
                changes.
              </p>
            )}
            <Continue onClick={() => markDoneAndAdvance('add_client')} label="Start" />
          </div>
        )}

        {def.key === 'brand_kit' && (
          <div className="card space-y-3 p-5">
            <p className="text-sm text-ink-2">
              No website yet — you’re just approving the look. Lock it and it becomes the foundation
              every section in <b>Build</b> is built on.
            </p>
            {/* #GR88: the admin live-brand carve-out banner — edits preview on the branch; the
                live site changes only via the explicit Go-live below. */}
            {adminLiveBrandEdit && (
              <div className="space-y-1 rounded border border-warning-bright bg-paper-2 p-2 text-sm text-warning">
                <p>
                  ⚠️ <b>{client.name} is live.</b> Brand edits are re-locked to a <b>preview</b> only —
                  nothing changes on the live site until you <b>publish</b> below.
                </p>
                {/* #GR89 Scope A: on a live site you can recolour/tweak what's there + re-lock;
                    adding new fonts or re-extracting the whole brand isn't available for a live site. */}
                <p className="text-xs text-warning">
                  On a live site you can adjust the existing colours, type &amp; grid and re-lock. Adding new
                  fonts or re-extracting the brand isn’t available for a live site.
                </p>
              </div>
            )}
            {/* #GR89: the font/artboard/Extract inputs stay is_live-403 (Scope A), so hide-by-disable
                them on the admin-live carve-out — nothing throws; recolour happens in the editor below. */}
            <BrandIntake
              clientId={client.id}
              disabled={busy || brandControlsLocked || adminLiveBrandEdit}
              fontFaces={tokens.typography.fontFaces ?? []}
              onFontFacesChange={(faces: FontFace[]) =>
                handleBrandTokens({ ...tokens, typography: { ...tokens.typography, fontFaces: faces } })
              }
              artboards={ws.drafts?.brandArtboards ?? []}
              onArtboardsChange={(paths) =>
                void persist({ ...ws, drafts: { ...(ws.drafts ?? {}), brandArtboards: paths } })
              }
              onExtracted={(t: TokenSchema) => handleBrandExtracted(t)}
              // Half B (co-equal door): merge the confirmed Figma mapping into the editor tokens via
              // the SAME draft path the AI-extraction uses → the existing Lock commits brand.ts.
              onFigmaConfirmed={(mapped) => handleBrandTokens(applyFigmaTokens(tokens, mapped))}
            />
            {paletteKept && (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-xs text-warning">
                No reference images this time — colours were kept from the current brand. Upload
                artboards and re-extract to re-derive the palette.
              </p>
            )}
            {/* #GR89: brand-preview also stays is_live-403 (Scope A) — and the admin-live flow has its
                own preview (the re-lock green gate below), so disable this standalone preview. */}
            <BrandPreview
              clientId={client.id}
              tokens={tokens}
              disabled={brandControlsLocked || adminLiveBrandEdit}
              latestPreviewBuildId={
                builds.find((b) => b.job_type === 'brand-preview' && b.outcome === 'success')?.id ?? null
              }
            />
            {/* Phase 4 item 2: the step's PRIMARY action — Lock — surfaced above the
                "Fine-tune" accordion so it isn't buried inside a collapsed panel. Same
                call + disabled logic + labels as BrandKit's own (now hidden via hideLock).
                In the admin-live carve-out this IS the re-lock (→ brand-relock) — identical
                behaviour, since it calls the same lockBrandKit. */}
            <button
              onClick={lockBrandKit}
              disabled={busy || brandControlsLocked}
              className={btnSolid}
            >
              <FlapText>{busy ? 'Saving…' : isDone('brand_kit') ? 'Re-lock the brand kit →' : 'Lock the brand kit →'}</FlapText>
            </button>
            <details className="rounded border border-line bg-paper">
              <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-ink">
                Fine-tune the specifics
              </summary>
              <div className="border-t border-line p-4">
                <BrandKit
                  tokens={tokens}
                  onChange={handleBrandTokens}
                  onLock={lockBrandKit}
                  busy={busy}
                  locked={isDone('brand_kit')}
                  isLive={brandControlsLocked}
                  hideLock
                />
              </div>
            </details>
            {/* #GR88: after an admin re-locks a LIVE client's brand → preview gate → publish.
                #GR90: render + resolve the ids from brandPublishResume (persisted build rows) so the
                publish affordance + chips survive a remount; optimistic click state takes precedence. */}
            {adminLiveBrandEdit && showBrandPublish && (
              <div className="space-y-2 rounded border border-warning-bright bg-paper p-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-ink-2">Brand preview:</span>
                  {effRelockBuildId && (
                    <BuildStatus
                      buildId={effRelockBuildId}
                      initialUrl={null}
                      live
                      onResolved={() => setBrandPreviewReady(true)}
                    />
                  )}
                </div>
                <p className={sectionDesc}>
                  Check the preview, then publish to update the live site.
                </p>
                <button
                  onClick={() => {
                    if (window.confirm(`Publish ${client.name}'s updated brand to the LIVE site?`)) void goLiveBrand();
                  }}
                  disabled={busy || !brandPreviewReady || !!effBrandGoliveBuildId}
                  className={btnSolid}
                  title={brandPreviewReady ? '' : 'Waiting for the preview to go green'}
                >
                  <FlapText>{effBrandGoliveBuildId ? 'Publishing…' : brandPreviewReady ? 'Publish to the live site →' : 'Publish (preview building…)'}</FlapText>
                </button>
                {effBrandGoliveBuildId && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-ink-2">Live:</span>
                    <BuildStatus
                      buildId={effBrandGoliveBuildId}
                      initialUrl={null}
                      live
                      endpoint="promote-status"
                      readyLabel="Live ✅"
                      openLabel="open live ↗"
                      onResolved={onActivity}
                    />
                  </div>
                )}
              </div>
            )}
            {err && <p className="text-sm text-danger">{err}</p>}
          </div>
        )}

        {def.key === 'site_structure' && (
          <div className="space-y-3">
            <p className="text-sm text-ink-2">
              The shared nav &amp; footer update everywhere automatically — no manual linking.
              Home always exists, and you can change this later inside <b>Build</b> too.
            </p>
            <div className="card p-5">
              <PageManager
                client={client}
                activePage={structurePage}
                onSwitchPage={setStructurePage}
                onActivity={onActivity}
                disabled={client.is_live && !maintainLive}
              />
            </div>
            {client.is_live && (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-sm text-warning">
                {maintainLive
                  ? 'Live — maintenance mode: page changes preview on the branch; the live site changes only on a deliberate publish.'
                  : '🔒 This site is live and protected — the page manager is read-only.'}
              </p>
            )}
            <Continue onClick={() => markDoneAndAdvance('site_structure')} label="Continue" />
          </div>
        )}

        {def.key === 'seo' && (
          <div className="card p-5">
            <BuildChat
              client={client}
              instance="seo"
              canMaintainLive={maintainLive}
              onActivity={onActivity}
              onContinue={() => markDoneAndAdvance('seo')}
              onSkip={() => go(STEPS[activeIdx + 1]?.key ?? def.key)}
            />
          </div>
        )}

        {def.key === 'assets' && (
          <div className="card p-5 text-sm">
            <div className="space-y-3">
              <p className="text-ink">
                Static site-chrome files the site uses — a client logo <b>SVG</b>, partner logos, a
                contact-page map, small graphics/icons. The build agent references them at
                <code className="mx-1 rounded bg-paper-2 px-1">/assets/&lt;name&gt;</code>. This is for
                site chrome, not portfolio — real photos/video go in <b>Content &amp; media</b>. This
                step is <b>optional</b>.
              </p>
              <AssetsUploader
                client={client}
                canMaintainLive={maintainLive}
                onIngested={() => {
                  markStepDone('assets');
                  onActivity();
                }}
              />
            </div>
            <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 flex items-center gap-2 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur">
              <button
                onClick={() => go('content_media')}
                className={btnGhost}
              >
                {isDone('assets') ? 'Continue →' : 'Skip for now →'}
              </button>
              {client.is_live &&
                (maintainLive ? (
                  <span className="text-xs text-warning">Live — maintenance mode: asset edits preview on the branch; publish to go live.</span>
                ) : (
                  <span className="text-xs text-warning">Site is live/protected — asset changes are off.</span>
                ))}
            </div>
          </div>
        )}

        {def.key === 'content_media' && (
          <div className="card p-5 text-sm">
            <div className="space-y-3">
              <p className="text-ink">
                <b>Optional</b> — anything you add here, the build uses instead of placeholders; skip it
                and Build uses neutral placeholder blocks you can replace later.
              </p>

              {/* One component owns the full vertical order: Step 1 (create galleries) →
                  Step 2 (uploader slot, its own catalogue hidden to de-dupe) → Galleries →
                  Catalogue (unassigned pool). The shared mediaVersion refresh still fires
                  both ways (#12): structuring's onChange and the uploader's
                  onCatalogueChange both bump it. */}
              <MediaStructuring
                client={client}
                version={mediaVersion}
                canMaintainLive={maintainLive}
                onChange={() => setMediaVersion((v) => v + 1)}
                uploader={
                  <MediaUploader
                    client={client}
                    version={mediaVersion}
                    canMaintainLive={maintainLive}
                    hideCatalogue
                    onIngested={() => {
                      markStepDone('content_media');
                      onActivity();
                    }}
                    onCatalogueChange={() => setMediaVersion((v) => v + 1)}
                  />
                }
              />
            </div>

            {/* #GR9(i): a persistently-reachable Continue that never scrolls out of reach
                at photographer scale. Sticky to the bottom of the step; behaviour unchanged
                (advances; only real ingestion marks the step done). */}
            <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 flex items-center gap-2 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur">
              <button
                onClick={() => go('site_structure')}
                className={btnGhost}
              >
                {isDone('content_media') ? 'Continue →' : 'Skip for now →'}
              </button>
              {client.is_live &&
                (maintainLive ? (
                  <span className="text-xs text-warning">Live — maintenance mode: media edits preview on the branch; publish to go live.</span>
                ) : (
                  <span className="text-xs text-warning">Site is live/protected — uploads are off.</span>
                ))}
            </div>
          </div>
        )}

        {def.key === 'review' && (
          // #21: the build engine is dormant (build-model.md) — sections are hand-built in the
          // Claude Code build session (③ lane), not in the app. This step is a hand-off signpost:
          // the current site + preview, and a Continue that marks review done so Preview & approve
          // stays reachable (a hand-build never fires the in-app section locks → reviewAllLocked
          // stays false; Continue must set steps.review='done', exactly like the other steps).
          <div className="space-y-3 card p-5 text-sm">
            <p className="text-ink">
              <b>{client.name}</b>’s sections are built in the Claude Code build session — not in the
              app. Build them there, then come back here to preview and go live.
            </p>
            {latestBuild ? (
              <div className="flex items-center gap-2">
                <span className="text-ink-2">Current site:</span>
                <BuildStatus
                  buildId={latestBuild.id}
                  initialUrl={latestBuild.preview_url}
                  live={
                    latestBuild.outcome === 'success' &&
                    !latestBuild.preview_url &&
                    isRecent(latestBuild.created_at)
                  }
                />
              </div>
            ) : (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-warning">
                No build yet — build the sections in the hand-build session, then come back here.
              </p>
            )}
            <Continue onClick={() => markDoneAndAdvance('review')} label="Continue" />
          </div>
        )}

        {def.key === 'preview_approve' && (
          <div className="space-y-3 card p-5 text-sm">
            <p className="text-ink">
              Share the live preview with <b>{client.name}</b> and get their sign-off before anything
              goes live. This is the client’s approval — distinct from the internal Build step.
            </p>
            {/* Decision C: gate on a GREEN preview of app/<slug> (the publish/lock path's single
                source of truth — /api/site-status, the branch's current deploy), not a build-row
                outcome. The chip polls it; onResolved flips previewReady when it first reads green. */}
            <div className="flex items-center gap-2">
              <span className="text-ink-2">Preview:</span>
              <BuildStatus
                buildId={latestBuild?.id ?? client.id}
                initialUrl={null}
                live
                endpoint="site-status"
                requestBody={{ clientId: client.id }}
                onResolved={() => setPreviewReady(true)}
              />
            </div>
            {clientApproved ? (
              <p className="rounded border border-success bg-paper-2 p-2 text-success">
                ✓ Client approved — ready for <b>Go live</b>.
              </p>
            ) : (
              <>
                {approvalStale && (
                  <p className="rounded border border-warning-bright bg-paper-2 p-2 text-warning">
                    The preview changed since <b>{client.name}</b> approved it — re-approve before going live.
                  </p>
                )}
                <button
                  onClick={recordApproval}
                  disabled={!previewReady}
                  className={btnSolid}
                >
                  <FlapText>{approvalStale ? 'Re-approve — continue →' : 'Client approved — continue →'}</FlapText>
                </button>
                {!previewReady && (
                  <p className={sectionDesc}>
                    The preview isn’t green yet — wait for the build to finish, then approve.
                  </p>
                )}
              </>
            )}

            {/* §5.3: surface the responsive-QA tick-count honestly next to approve so "approve with
                zero checks" is at least visible. Advisory — it does NOT gate (the checklist's own contract). */}
            <p className={sectionDesc}>
              Responsive QA: <b>{viewportsChecked}/{viewportsTotal}</b> viewports checked — advisory, doesn’t block go-live.
            </p>

            {/* Responsive QA checklist (responsive-checklist.md) — advisory; a launcher, not an iframe. */}
            <ResponsiveChecklist client={client} ws={ws} canMaintainLive={maintainLive} onPersist={(next) => void persist(next)} />

            {/* Go-live checklist (go-live-checklist.md) — the per-client launch checks; surfaced again
                at Go live. Items 1 + 9 roll up the responsive + client-approval state above. */}
            <GoLiveChecklist
              client={client}
              ws={ws}
              canMaintainLive={maintainLive}
              rollups={goliveRollups}
              onPersist={(next) => void persist(next)}
            />
          </div>
        )}

        {def.key === 'send_live' && (
          <div className="space-y-3 card p-5 text-sm">
            <p className="text-ink">
              {showPublishUpdate ? (
                <>You’ve approved an update — <b>publish it live</b> below.</>
              ) : isAdmin && !client.is_live && !hasPromoted ? (
                <>Your client has approved the preview — <b>publish it live</b> below.</>
              ) : (
                <>
                  Your client has approved the preview — <b>Neil takes it live</b>. You don’t need to do
                  anything else here; this page updates once it’s published.
                </>
              )}
            </p>
            {showPublishUpdate ? (
              // Model-A maintenance re-publish: a live, maintainable client with a fresh approval.
              // Reuses goLive() verbatim (→ /api/go-live, re-publishes a live client: is_live stays
              // true, no-op-cleans if nothing changed). Tracked via the same effSendLiveBuildId chip.
              <div className="space-y-2">
                <button
                  onClick={() => {
                    const base = `Publish the approved update to ${client.name}'s live site?`;
                    const msg =
                      goliveStatus.open > 0
                        ? `${goliveStatus.open} go-live checklist item${goliveStatus.open === 1 ? ' is' : 's are'} still open.\n\n${base}\n\nPublish anyway?`
                        : base;
                    if (window.confirm(msg)) void goLive();
                  }}
                  disabled={busy || !!effSendLiveBuildId}
                  className={btnSolid}
                >
                  <FlapText>{effSendLiveBuildId ? 'Publishing…' : 'Publish update →'}</FlapText>
                </button>
                {effSendLiveBuildId && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-ink-2">Live:</span>
                    <BuildStatus
                      buildId={effSendLiveBuildId}
                      initialUrl={null}
                      live
                      endpoint="promote-status"
                      readyLabel="Live ✅"
                      openLabel="open live ↗"
                      onResolved={onActivity}
                    />
                  </div>
                )}
                {err && <p className="text-sm text-danger">{err}</p>}
              </div>
            ) : hasPromoted ? (
              <div className="flex items-center gap-2">
                <span className="text-ink-2">Live site:</span>
                <BuildStatus
                  buildId={builds.find((b) => b.promote_commit_sha)!.id}
                  initialUrl={builds.find((b) => b.promote_commit_sha)!.production_url}
                  live={
                    !builds.find((b) => b.promote_commit_sha)!.production_url &&
                    isRecent(
                      builds.find((b) => b.promote_commit_sha)!.promoted_at ??
                        builds.find((b) => b.promote_commit_sha)!.created_at,
                    )
                  }
                  endpoint="promote-status"
                  readyLabel="Live ✅"
                  openLabel="open live ↗"
                  onResolved={onActivity}
                />
              </div>
            ) : isAdmin && !client.is_live ? (
              // Item 1 (Phase 4): admin can actually publish from the wizard — the proven
              // promote path (goLive → 'promote'), behind a confirm, tracked to Live below.
              <div className="space-y-2">
                <button
                  onClick={() => {
                    // Soft gate (go-live-checklist.md): never go live SILENTLY with open checklist
                    // items — surface them in the confirm. Admin can still override by accepting.
                    const base = `Take ${client.name} live? This publishes the approved site to production.`;
                    const msg =
                      goliveStatus.open > 0
                        ? `${goliveStatus.open} go-live checklist item${goliveStatus.open === 1 ? ' is' : 's are'} still open.\n\n${base}\n\nGo live anyway?`
                        : base;
                    if (window.confirm(msg)) void goLive();
                  }}
                  disabled={busy || !!effSendLiveBuildId}
                  className={btnSolid}
                >
                  <FlapText>{effSendLiveBuildId ? 'Going live…' : 'Go live →'}</FlapText>
                </button>
                {effSendLiveBuildId && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-ink-2">Live:</span>
                    <BuildStatus
                      buildId={effSendLiveBuildId}
                      initialUrl={null}
                      live
                      endpoint="promote-status"
                      readyLabel="Live ✅"
                      openLabel="open live ↗"
                      onResolved={onActivity}
                    />
                  </div>
                )}
                {err && <p className="text-sm text-danger">{err}</p>}
              </div>
            ) : (
              <p className="rounded border border-warning-bright bg-paper-2 p-2 text-warning">
                Waiting for Neil to review and publish. Give him a nudge when the preview’s ready.
              </p>
            )}

            {/* Surface the go-live checklist again right before publishing (go-live-checklist.md) —
                the launch items (astro.config site, DNS, DNSSEC/SSL, analytics, holding→real) live
                here. Hidden once the site is live (the checks are pre-launch). */}
            {!hasPromoted && (
              <GoLiveChecklist
                client={client}
                ws={ws}
                canMaintainLive={maintainLive}
                rollups={goliveRollups}
                onPersist={(next) => void persist(next)}
              />
            )}
          </div>
        )}

        {def.key === 'handover' && (
          <div className="space-y-3 card p-5 text-sm">
            <p className="text-ink">Last step — a quick wrap-up before you hand the site off.</p>
            <ul className="space-y-1 text-ink-2">
              <li>{isDone('brand_kit') ? '✓' : '○'} Brand kit locked</li>
              <li>{isDone('review') ? '✓' : '○'} Sections built &amp; locked</li>
              <li>{isDone('preview_approve') ? '✓' : '○'} Client approved the preview</li>
              <li>{hasPromoted ? '✓' : '○'} Site live</li>
            </ul>
            {/* Phase 4 item 7: an honest advisory reminder — not a checkbox. The old
                "○ Anything client-specific noted for Neil" was a ○ with no data behind
                it, so it could never tick (a false affordance). The four items above are
                real (derived from isDone/hasPromoted). */}
            <p className={sectionDesc}>Before you finish: note anything client-specific for Neil.</p>
            {ws.handover_done ? (
              <p className="rounded border border-success bg-paper-2 p-2 text-success">
                ✓ Handover complete — this client is fully set up and live.
              </p>
            ) : (
              <button
                onClick={markHandover}
                className={btnSolid}
              >
                <FlapText>Mark handover complete</FlapText>
              </button>
            )}
          </div>
        )}

      </section>
    </div>
  );
}

function Continue({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={btnSolid}
    >
      <FlapText>{label} →</FlapText>
    </button>
  );
}
